import { useCallback } from 'react'

import {
  QueryClient,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import { Draft, produce } from 'immer'

import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_AUTH_USER } from '~/constants/refetch-intervals'
import { useServerRequests } from '~/hooks/useRequests'
import { useCookieStore } from '~/store/cookieStore'
import { AuthUser, SmartAccountsSetupStep, UserFlags } from '~/types'
import { setSentryUserId } from '~/utils/errors'
import { isSplitsEmail, setTrackingUserProperties } from '~/utils/tracking'

export const useRequiredAuthUser = () => {
  const isDev = process.env.APP_ENV !== 'production'
  const { user: authUser } = useAuthUser()
  if (!authUser)
    throw new Error(
      'Auth user required.' +
        (isDev ? " Don't use this hook in non-authed routes." : ''),
    )
  return authUser
}

export const useAuthUser = (
  options: {
    refetchOnMount?: UseQueryOptions<AuthUser>['refetchOnMount']
  } = {},
) => {
  const { refetchOnMount = undefined } = options
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useCookieStore((s) => s.activeOrgId)
  const setActiveOrgId = useCookieStore((s) => s.setActiveOrgId)
  const authToken = useCookieStore((s) => s.authToken)
  const isClient = typeof window !== 'undefined'

  const fetchUser = useCallback(async () => {
    const result = await serverGetRequest<{
      user: AuthUser
    }>({
      path: '/api/user',
    })

    if (!result) {
      throw new Error('Failed to fetch user')
    }

    setTrackingUserProperties({
      userId: result.data.user.id,
      isTestUser: isSplitsEmail(result.data.user.email),
    })

    setSentryUserId(result.data.user.id)

    const hasFinishedOrg = result.data.user.orgs.some(
      (org) =>
        !org.isPersonal &&
        (!org.setupCurrentStep ||
          org.setupCurrentStep === SmartAccountsSetupStep.COMPLETED ||
          org.setupCurrentStep === SmartAccountsSetupStep.SKIPPED),
    )

    const workspaceOrgs = result.data.user.orgs
      .filter((org) => org.isPersonal === false)
      // NOTE: currently preventing users from viewing SKIPPED orgs (compliance payees)
      .filter((org) => org.setupCurrentStep !== 'SKIPPED')

    const user = {
      ...result.data.user,
      orgs: workspaceOrgs,
      hasFinishedOrg,
    }

    if (!activeOrgId && workspaceOrgs[0]) {
      setActiveOrgId(workspaceOrgs[0].id)
    }

    return user
  }, [activeOrgId, serverGetRequest, setActiveOrgId])

  const query = useQuery<AuthUser>({
    queryKey: [QUERY_KEYS.AUTH_USER],
    enabled: !!authToken,
    queryFn: fetchUser,
    refetchInterval: REFETCH_AUTH_USER,
    refetchOnMount,
  })

  // NOTE: Used to handle render loading state on client side, even when the query is not enabled
  let initialLoadingState = true
  if (query.data) initialLoadingState = false
  if (query.error) initialLoadingState = false
  if (!authToken && isClient) initialLoadingState = false

  return {
    // A persisted user without a token is a dead session — don't let it enable
    // auth-gated queries on boot.
    user: authToken ? query.data : undefined,
    isLoading: query.isLoading,
    isFetchedAfterMount: query.isFetchedAfterMount,
    initialLoadingState,
  }
}

export const useAuthUserUpdates = () => {
  const queryClient = useQueryClient()

  const updateAuthUser = useCallback(
    (recipe: (user: Draft<AuthUser>) => void) => {
      updateAuthUserInQueryCache(queryClient, recipe)
    },
    [queryClient],
  )

  return {
    updateDisplayName: useCallback(
      (name: string) => {
        updateAuthUser((user) => {
          user.displayName = name
        })
      },
      [updateAuthUser],
    ),
    updateProfilePhotoUrl: useCallback(
      (profilePhotoUrl: string) => {
        updateAuthUser((user) => {
          user.profilePhotoUrl = profilePhotoUrl
        })
      },
      [updateAuthUser],
    ),
    updateUserFlags: useCallback(
      (flags: UserFlags) => {
        updateAuthUser((user) => {
          user.splitsConnectDetectedAt = flags.splitsConnectDetectedAt
          user.extensionsPageViewedAt = flags.extensionsPageViewedAt
        })
      },
      [updateAuthUser],
    ),
  }
}

const updateAuthUserInQueryCache = (
  queryClient: QueryClient,
  recipe: (authUser: Draft<AuthUser>) => void,
) => {
  queryClient.setQueryData<AuthUser>([QUERY_KEYS.AUTH_USER], (data) => {
    if (data) {
      return produce(data, (draft) => recipe(draft))
    }

    return data
  })
}
